import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Skeleton,
  Spinner,
  Stack,
  Switch,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { OurWorkItems } from "@components/modules/projects/detail/BudgetItems/OurWork/OurWorkList";
import { Trans, t } from "@lingui/macro";
import { ProjectStatusEnum } from "@src/__generated__/urql-graphql";
import { ProjectDetailLoading } from "@src/components/modules/projects/common/project-detail-loading";
import OurWorkHeader from "@src/components/modules/projects/detail/BudgetItems/OurWork/OurWorkHeader";
import { ProjectEvaluation } from "@src/components/modules/projects/detail/Evaluation";
import { BillingBoxCard } from "@src/components/modules/projects/detail/Stats/billing-box";
import { TasksBulkActionsModal } from "@src/components/modules/resource-planning/tasks/listing/components/bulk-actions-modal";
import { TaskListingBulkActionsStore } from "@src/components/modules/resource-planning/tasks/listing/store/bulk-actions";
import { LoadingOverlay } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { BulkActions } from "@src/components/widgets/bulk-actions";
import { NotFoundProject } from "@src/components/widgets/ErrorPage";
import { can, withCan } from "@src/utils/components/permissions";
import { headTitle } from "@src/utils/head-title";
import { useScrollToHashID, useStore } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import Head from "next/head";
import React, { type FunctionComponent, useLayoutEffect } from "react";
import { Banners } from "./Banners";
import { Expense } from "./BudgetItems";
import { BUDGET_ITEM_HASH_INDEX_SEPARATOR } from "./BudgetItems/ItemTitle";
import { CardEmpty } from "./CardEmpty";
import { ProjectCreateBudgetItemTypeEnum } from "./create-budget-item-modal/store";
import { CreateTasksFromTaskTemplateGroupModal } from "./create-tasks-from-task-template-group-modal";
import { Header } from "./Header";
import { useFetchProject } from "./hooks";
import { PlannedExpensesModal } from "./planned-expenses-modal";
import { ExpenseStatsCard, OurWorkStatsCard } from "./Stats";
import { Store, StoreContext } from "./Store";
import { Tab } from "./Tab";

interface DetailProps {
  id: string;
}

const Detail: FunctionComponent<DetailProps> = ({ id }) => {
  const appStore = useStore();
  const { workspaceStore, projectCreateBudgetItemModalStore } = appStore;
  const { store } = useLocalObservable(() => ({
    store: new Store(
      new TaskListingBulkActionsStore(appStore, () => {
        store.refetchProject?.();
      }),
    ),
  }));

  const { data, loading } = useFetchProject(id, store);

  useLayoutEffect(() => {
    if (workspaceStore.settings?.show_done_tasks) {
      store.showDoneTasks.on();
      if (store.project?.ourWorkOverview.length) {
        void (async () => {
          await store.fetchDoneTasks();
          store.setBudgetItemsShowDoneTasks(true);
        })();
      }
      return;
    }

    store.showDoneTasks.off();
    store.setBudgetItemsShowDoneTasks(false);
  }, [workspaceStore.settings?.show_done_tasks]);

  useLayoutEffect(() => {
    if (!location.hash) return;
    const tabIndex = location.hash.split(BUDGET_ITEM_HASH_INDEX_SEPARATOR)?.[1];
    if (!tabIndex) return;
    store.setBudgetCategoryTabIndex(Number(tabIndex));
  }, []);

  useScrollToHashID(!loading && Boolean(data?.project));

  if (loading) {
    return <ProjectDetailLoading id={id} />;
  }

  if (!loading && !data?.project) {
    return <NotFoundProject />;
  }

  return (
    <LoadingOverlay isLoading={loading}>
      <Head>
        <title>{headTitle(store.project?.title)}</title>
      </Head>

      {store?.project && (
        <StoreContext.Provider value={store}>
          <Banners />
          <Header />

          {(store.project.status === ProjectStatusEnum.Finished ||
            store.project.status === ProjectStatusEnum.Archived) && (
            <ProjectEvaluation project={store.project} />
          )}

          {store.hasOurWorkItems || store.project.hasAnyExpenseItem ? (
            <React.Fragment>
              <Grid
                gap="4"
                templateColumns={{
                  base: "1fr",
                  lg: store.project.isTemplate ? "1fr" : "0.35fr 0.65fr",
                }}
              >
                <GridItem>
                  <Stack
                    dir={store.project.isTemplate ? "row" : "column"}
                    spacing="4"
                    h="full"
                  >
                    <OurWorkStatsCard
                      project={store.project}
                      isLoading={store.isOurWorkOverviewLoading}
                    />
                    {can(
                      "expense_read_all",
                      "expense_read_team",
                      "expense_read_own",
                    ) && <ExpenseStatsCard project={store.project} />}
                  </Stack>
                </GridItem>

                <GridItem>
                  {!store.project.isTemplate && <BillingBoxCard />}
                </GridItem>
              </Grid>

              {(store.hasOurWorkItems ||
                store.project.expensesStructured.some(
                  (expense) => expense.hasBudgetItems,
                )) && (
                <Box mt="8" mb="14">
                  <Tabs
                    index={store.selectedBudgetCategoryTabIndex}
                    onChange={store.setBudgetCategoryTabIndex}
                  >
                    <TabList
                      borderBottom="unset"
                      display="flex"
                      flexDir={{ base: "column-reverse", md: "row" }}
                    >
                      <HStack spacing="0">
                        {store.hasOurWorkItems && (
                          <Tab>
                            {store.isOurWorkOverviewLoading && (
                              <Spinner mr="2" size="sm" />
                            )}
                            <Trans>Work</Trans>
                          </Tab>
                        )}

                        {can(
                          "expense_read_all",
                          "expense_read_team",
                          "expense_read_own",
                        ) &&
                          store.project.expensesStructuredFiltered.map(
                            (expense, index) => (
                              <Tab key={expense.name + index.toString()}>
                                <Trans>Expenses ({expense.name})</Trans>
                              </Tab>
                            ),
                          )}
                        {store.project.isTimeAndMaterials && (
                          <Button
                            alignSelf="center"
                            ml="4"
                            colorScheme="grey"
                            onClick={() => {
                              projectCreateBudgetItemModalStore.modalState.onOpen(
                                {
                                  projectId: id,
                                  budgetItemType: store.isOurWorkTabSelected
                                    ? ProjectCreateBudgetItemTypeEnum.Work
                                    : ProjectCreateBudgetItemTypeEnum.Expense,
                                  expenseBudgetCategoryId:
                                    store.selectedTabExpenseBudgetCategoryId,
                                  onCreate: store.onCreateBudgetItem,
                                },
                              );
                            }}
                            size="sm"
                            variant="outline"
                          >
                            <Trans>New budget item</Trans>
                          </Button>
                        )}
                      </HStack>

                      {store.isOurWorkTabSelected && (
                        <Flex
                          align="center"
                          justify={{ base: "flex-start", md: "flex-end" }}
                          flex="1"
                          ml={{ base: "0", md: "6" }}
                          mb={{ base: "4", md: "0" }}
                        >
                          {!store.isReorderModeToggled ? (
                            <HStack
                              spacing="2"
                              flexDir={{ base: "row-reverse", md: "row" }}
                            >
                              <HStack
                                spacing="2"
                                flexDir={{ base: "column-reverse", md: "row" }}
                                align={{ base: "flex-start", md: "center" }}
                              >
                                {store.hasHiddenOurWorkItems && (
                                  <FormControl
                                    alignItems="center"
                                    display="flex"
                                    w="max-content"
                                  >
                                    <FormLabel
                                      display="inline-flex"
                                      mb="0"
                                      htmlFor="show-hidden-budget-items-toggle"
                                    >
                                      <Trans>Show hidden budget items</Trans>
                                      &nbsp;
                                      <Text>({store.hiddenOwbiCount})</Text>
                                    </FormLabel>
                                    <Switch
                                      id="show-hidden-budget-items-toggle"
                                      isChecked={store.showHiddenOwbi?.value}
                                      onChange={() => {
                                        store.showHiddenOwbi?.toggle();
                                      }}
                                    />
                                  </FormControl>
                                )}

                                <FormControl
                                  alignItems="center"
                                  display="flex"
                                  w="max-content"
                                >
                                  <FormLabel
                                    display="inline-flex"
                                    mb="0"
                                    htmlFor="show-done-tasks-toggle"
                                  >
                                    <Trans>Show done tasks</Trans>
                                    &nbsp;
                                    <Text>({store.doneTaskCount})</Text>
                                  </FormLabel>
                                  <Switch
                                    id="show-done-tasks-toggle"
                                    isChecked={store.showDoneTasks?.value}
                                    onChange={() => {
                                      store.toggleShowDoneTasks();
                                    }}
                                  />
                                </FormControl>
                              </HStack>

                              <Tooltip
                                label={t`Reorder & hide budget items`}
                                shouldWrapChildren
                              >
                                <IconButton
                                  aria-label={t`Reorder mode toggle`}
                                  colorScheme="grey"
                                  icon={<Icon name="settings-01" />}
                                  onClick={store.toggleReorderMode}
                                  variant="ghost"
                                />
                              </Tooltip>
                            </HStack>
                          ) : (
                            <HStack spacing="2">
                              <Button
                                colorScheme="grey"
                                isDisabled={store.updatingBudgetItems}
                                onClick={() => {
                                  store.restoreOurWorkBudgetItems();
                                  store.setReorderMode(false);
                                }}
                                size="sm"
                                variant="ghost"
                              >
                                <Trans>Cancel</Trans>
                              </Button>
                              <Button
                                isLoading={store.updatingBudgetItems}
                                onClick={store.updateOurWorkOverviewBudgetItems}
                                size="sm"
                              >
                                <Trans>Save</Trans>
                              </Button>
                            </HStack>
                          )}
                        </Flex>
                      )}
                    </TabList>
                    <TabPanels>
                      {store.hasOurWorkItems && (
                        <TabPanel pos="relative" p="0">
                          <LoadingOverlay
                            position="absolute"
                            isLoading={store.isOurWorkOverviewLoading}
                            size="md"
                          >
                            {store.isOurWorkOverviewLoading && (
                              <OurWorkHeader />
                            )}
                            {store.project.ourWorkOverview.map((ourWork) => (
                              <OurWorkItems
                                key={ourWork.id}
                                structuredItem={ourWork}
                                tabIndex={0}
                              />
                            ))}
                            {store.isOurWorkOverviewLoading && (
                              <VStack
                                w="full"
                                px="4"
                                py="3"
                                bg="teal.50"
                                spacing="4"
                              >
                                <Skeleton w="full" h="16" />
                                <Skeleton w="full" h="8" />
                              </VStack>
                            )}
                          </LoadingOverlay>
                        </TabPanel>
                      )}
                      {can(
                        "expense_read_all",
                        "expense_read_team",
                        "expense_read_own",
                      ) &&
                        store.project.expensesStructuredFiltered.map(
                          (expense, index) => (
                            <TabPanel key={expense.id} p="0">
                              <Expense
                                structuredItem={expense}
                                tabIndex={
                                  index +
                                  (store.project?.ourWorkOverview.length ?? 1)
                                }
                              />
                            </TabPanel>
                          ),
                        )}
                    </TabPanels>
                  </Tabs>
                </Box>
              )}
            </React.Fragment>
          ) : (
            <CardEmpty />
          )}
          <CreateTasksFromTaskTemplateGroupModal
            store={store.createTaskFromTemplateGroupStore}
          />
          <PlannedExpensesModal />
          <BulkActions store={store.taskListingBulkActionsStore} />
          <TasksBulkActionsModal
            store={store.taskListingBulkActionsStore}
            defaultProjectId={store.defaultProjectIdForBulkActions}
            defaultProjectOptions={store.defaultProjectOptionForBulkActions}
            defaultBudgetItemId={store.defaultBudgetItemIdForBulkActions}
            defaultBudgetItemOptions={
              store.defaultBudgetItemOptionsForBulkActions
            }
          />
        </StoreContext.Provider>
      )}
    </LoadingOverlay>
  );
};

export default withCan(
  { do: ["project_read_own", "project_read_all", "project_read_team"] },
  observer(Detail),
);
